import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, LoadScript, Marker, StandaloneSearchBox } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { LabelStyled } from "../../ui/Elements";
import { useForm } from "react-hook-form";
const libraries = ["places"];
const defaultCenter = { lat: 24.7136, lng: 46.6753 };
const MapSearchSelect = ({ 
    label, 
    locationName, 
    latName, 
    lngName, 
    registerMap, 
    setValue,
    errors,
    defaultAddress,
    validateBeforeNext // ✅ Function to check if the step can proceed
}) => {
    const { t } = useTranslation();
    const [map, setMap] = useState(null);
    const { clearErrors } = useForm();
    const [markerPosition, setMarkerPosition] = useState(defaultCenter);
    const [searchBox, setSearchBox] = useState(null);
    const [address, setAddress] = useState(defaultAddress || "");
    const [isValidLocation, setIsValidLocation] = useState(false); // ✅ Track if a valid location is selected

    const searchInputRef = useRef(null);

    useEffect(() => {
        if (searchInputRef.current && map) {
            const searchBox = new window.google.maps.places.SearchBox(searchInputRef.current);
            searchBox.setBounds(new window.google.maps.LatLngBounds(
                { lat: 24.5815, lng: 46.4063 }, // Approximate bounds of Riyadh
                { lat: 24.9360, lng: 46.9784 }
            ));
            
            setSearchBox(searchBox);

            searchBox.addListener("places_changed", () => {
                const places = searchBox.getPlaces();
                if (places.length === 0) return;
                const place = places[0];
                if (!place.geometry || !place.geometry.location) return;

                //  Ensure location is inside Riyadh
                if (!place.formatted_address.includes("Riyadh")) {
                    // alert("Please select a location within Riyadh, Saudi Arabia.");
                    setIsValidLocation(false); // ❌ Mark location as invalid
                    return;
                }

                // ✅ Mark location as valid
                setIsValidLocation(true);
                setAddress(place.formatted_address);

                const newLocation = {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                };

                setMarkerPosition(newLocation);
                setValue(locationName, place.formatted_address, { shouldValidate: true });
                setValue(latName, newLocation.lat);
                setValue(lngName, newLocation.lng);
                registerMap(locationName).onChange({
                    target: { name: locationName, value: place.formatted_address },
                });

                map.panTo(newLocation);
                map.setZoom(15);
            });
        }
    }, [map]);

    const handleMapClick = (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setMarkerPosition({ lat, lng });

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
                const selectedAddress = results[0].formatted_address;

                // ✅ Ensure location is inside Riyadh
                if (!selectedAddress.includes("Riyadh")) {
                    // alert("Please select a location within Riyadh, Saudi Arabia.");
                    setIsValidLocation(false); // ❌ Mark as invalid
                    return;
                }

                // ✅ Mark location as valid
                setIsValidLocation(true);
                setAddress(selectedAddress);

                setValue(locationName, selectedAddress, { shouldValidate: true });
                setValue(latName, lat);
                setValue(lngName, lng);
                registerMap(locationName).onChange({
                    target: { name: locationName, value: selectedAddress },
                });
            }
        });
    };

    

    return (
        <LoadScript googleMapsApiKey="AIzaSyAn4_Gkxrr-S7J9-tLzrZM6fVOFZ9t2CPI" libraries={libraries}>
            <AddressWrapper className="col-md-12 form-group addressWrapper d-flex gap-1 flex-wrap">
                <LabelStyled className='pt-3 pb-0'>{label}</LabelStyled>
                <input 
                    {...registerMap(locationName, { required: "Location is required" })} 
                    type="text"
                    ref={searchInputRef}
                    className={`form-control ${errors?.[locationName] ? 'is-invalid' : ''}`}
                    placeholder={t("SelectLocation")}
                    value={address}
                    onChange={(e) => {
                        setAddress(e.target.value);
                        registerMap(locationName).onChange(e);
                        if (e.target.value.trim() === "") {
                            setValue(locationName, "");
                        }
                    }}
                />
                {errors?.[locationName] && <span className="locationError text-danger">{errors[locationName].message}</span>}
                
                <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "250px" }}
                    center={markerPosition}
                    zoom={12}
                    onLoad={(mapInstance) => setMap(mapInstance)}
                    onClick={handleMapClick}
                >
                    <Marker position={markerPosition} draggable={true} onDragEnd={handleMapClick} />
                </GoogleMap>

                <input type="hidden" {...registerMap(latName)} value={markerPosition.lat} />
                <input type="hidden" {...registerMap(lngName)} value={markerPosition.lng} />
            </AddressWrapper>
        </LoadScript>
    );
};
const AddressWrapper = styled.div`
    input[type="text"]{
        height: 48px;
        color: var(--darkGrey) !important;
        border-radius: 6px;
        background: var(--white);
        border: 1px solid var(--border);
        font-size: 14px;
        font-family: var(--regular);
        margin-bottom: 7px;
        body.rtl &{
            font-family: var(--arRegular);
        }
    }
    & > div:nth-child(2){
        border-radius: 6px;
        overflow: hidden;
    }
    .locationError{
        font-size: 14px;
        font-family: var(--regular);
        margin-top: -10px;
        body.rtl &{
            font-family: var(--arRegular);
        }
    }
`
export default MapSearchSelect;
